import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Video as VideoIcon, X, Repeat } from 'lucide-react';

// The picture to show for a post. A video post's media_url is the video FILE (an r2-fetch URL or a
// bare R2 key), which an <img> cannot draw - every posted video showed as a blank tile (Rebecca,
// Oct 25, 2026). So: the stored thumbnail first, then media_url only when it is not a video, else
// nothing (the play-icon placeholder).
const VIDEO_RE = /\.(mp4|mov|m4v|webm)(\?|&|%|$)|r2-fetch|media-library%2Fvideos|^media-library\/videos\//i;
function postImage(p) {
  if (p.thumb_url) return p.thumb_url;
  if (p.media_type === 'video' || VIDEO_RE.test(String(p.media_url || ''))) return null;
  return p.media_url || null;
}

// Shared content calendar. Shows scheduled + published + draft posts on a month
// grid. Entries are color-coded by PLATFORM (Facebook / Instagram / TikTok); the
// post's status is a secondary cue (reduced opacity for anything not yet
// published, plus a status label in the detail popover). Data rows must carry a
// `platform` field ('facebook' | 'instagram' | 'tiktok').

// Platform is the primary color. Each platform gets a chip tint + accent.
const PLATFORM = {
  facebook:  { color: '#1877F2', tint: '#eaf2fe', border: '#bcdcff', label: 'FB' },
  instagram: { color: '#E1306C', tint: '#fdeef4', border: '#f3c9dc', label: 'IG' },
  tiktok:    { color: '#111111', tint: '#f0f0f2', border: '#d5d5db', label: 'TikTok' },
};
function plat(p) { return PLATFORM[p && p.platform] || PLATFORM.facebook; }

// Poster's initials from created_by (the logged-in name at publish, e.g.
// "Greg Cain" -> "GC"). Blank if there's no usable name.
function initialsOf(name) {
  const s = String(name || '').trim();
  if (!s || !/[a-z]/i.test(s)) return '';
  const parts = s.split(/\s+/).filter(Boolean);
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Status is the secondary cue (label in the popover; opacity in the grid).
const STATUS = {
  scheduled: { dot: '#d68a00', chip: '#fff8e6', label: 'Scheduled' },
  published: { dot: '#1a8a4a', chip: '#e9f9f0', label: 'Published' },
  publishing:{ dot: '#1a6fb0', chip: '#eaf4fd', label: 'Publishing' },
  failed:    { dot: '#c0392b', chip: '#fdecea', label: 'Failed' },
  canceled:  { dot: '#9aa0a6', chip: '#f3f4f5', label: 'Canceled' },
  // TikTok draft statuses
  queued:    { dot: '#9a5b00', chip: '#fff4e8', label: 'Queued' },
  uploading: { dot: '#9a5b00', chip: '#fff4e8', label: 'Uploading' },
  in_drafts: { dot: '#1a8a4a', chip: '#e9f8ef', label: 'In drafts' },
};
function statusInfo(s) { return STATUS[s] || { dot: '#9aa0a6', chip: '#f3f4f5', label: s || 'Status' }; }
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function postDate(p) {
  const d = p.scheduled_for || p.published_at || p.created_at;
  return d ? new Date(d) : null;
}
function dayKey(d) { return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; }
function rowKey(p) { return `${p.platform || 'fb'}-${p.id}`; }
function fmtTime(p) {
  const d = postDate(p);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function PostCalendar({ posts = [], onCancel }) {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [sel, setSel] = useState(null);

  const byDay = {};
  posts.forEach(p => { const d = postDate(p); if (d) { const k = dayKey(d); (byDay[k] = byDay[k] || []).push(p); } });
  Object.values(byDay).forEach(list => list.sort((a, b) => (postDate(a) - postDate(b))));

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = new Date(monthStart); gridStart.setDate(1 - monthStart.getDay());
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const todayKey = dayKey(now);
  const monthLabel = cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const move = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));

  const navBtn = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer', padding: 4, display: 'inline-flex', color: 'var(--navy)' };

  return (
    <div style={{ position: 'relative' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button style={navBtn} onClick={() => move(-1)}><ChevronLeft size={16} /></button>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', minWidth: 150, textAlign: 'center' }}>{monthLabel}</div>
          <button style={navBtn} onClick={() => move(1)}><ChevronRight size={16} /></button>
          <button style={{ ...navBtn, fontSize: 11, fontWeight: 600, padding: '4px 10px' }} onClick={() => setCursor(new Date(now.getFullYear(), now.getMonth(), 1))}>Today</button>
        </div>
        {/* platform legend (primary color) + a note that faded = not yet published */}
        <div style={{ display: 'flex', gap: 12, fontSize: 10.5, color: 'var(--gray-500)', alignItems: 'center', flexWrap: 'wrap' }}>
          {['facebook', 'instagram', 'tiktok'].map(k => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: PLATFORM[k].color, display: 'inline-block' }} /> {PLATFORM[k].label}
            </span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: 0.6 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#9aa0a6', display: 'inline-block' }} /> faded = scheduled / draft
          </span>
        </div>
      </div>

      {/* weekday row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
        {WEEKDAYS.map(w => <div key={w} style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', textAlign: 'center', padding: '4px 0' }}>{w}</div>)}
      </div>

      {/* day grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1, background: 'var(--gray-200)', border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden' }}>
        {days.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const k = dayKey(d);
          const isToday = k === todayKey;
          const items = byDay[k] || [];
          return (
            <div key={i} style={{ background: inMonth ? '#fff' : '#fafafa', minHeight: 96, padding: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ fontSize: 11, fontWeight: isToday ? 800 : 600, color: isToday ? 'var(--orange)' : inMonth ? 'var(--navy)' : 'var(--gray-300, #c8ccd0)', textAlign: 'right', paddingRight: 2 }}>
                {isToday ? <span style={{ background: 'var(--orange)', color: '#fff', borderRadius: 10, padding: '1px 6px' }}>{d.getDate()}</span> : d.getDate()}
              </div>
              {items.slice(0, 3).map(p => {
                const pl = plat(p);
                const published = p.status === 'published';
                const failed = p.status === 'failed';
                return (
                  <div key={rowKey(p)} onClick={() => setSel(p)} title={`${pl.label} · ${statusInfo(p.status).label}${(p.caption || p.source_title) ? ` — ${(p.caption || p.source_title).replace(/\s+/g, ' ')}` : ''}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: pl.tint, border: `1px solid ${pl.border}`, borderLeft: `3px solid ${pl.color}`, borderRadius: 4, padding: '2px 4px', cursor: 'pointer', minWidth: 0, opacity: published ? 1 : 0.62 }}>
                    {postImage(p)
                      ? <img src={postImage(p)} alt="" style={{ width: 16, height: 16, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} />
                      : <span style={{ width: 16, height: 16, borderRadius: 2, background: '#dfe3e8', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><VideoIcon size={10} color="#5f6368" /></span>}
                    <span style={{ fontSize: 9.5, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{fmtTime(p)} {p.source_video_id ? '↻ ' : ''}{(p.caption || p.source_title || 'post').replace(/\s+/g, ' ').slice(0, 20)}</span>
                    {initialsOf(p.created_by) && <span title={`Posted by ${p.created_by}`} style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: pl.color, borderRadius: 8, padding: '0 3px', flexShrink: 0, lineHeight: 1.7 }}>{initialsOf(p.created_by)}</span>}
                    {failed && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c0392b', flexShrink: 0 }} />}
                  </div>
                );
              })}
              {items.length > 3 && <div style={{ fontSize: 9.5, color: 'var(--gray-500)', paddingLeft: 2 }}>+{items.length - 3} more</div>}
            </div>
          );
        })}
      </div>

      {/* detail popover */}
      {sel && (
        <>
          <div onClick={() => setSel(null)} style={{ position: 'fixed', inset: 0, zIndex: 200 }} />
          <div style={{ position: 'absolute', top: 40, left: '50%', transform: 'translateX(-50%)', zIndex: 201, width: 340, maxWidth: '92%', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.18)', padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4, background: plat(sel).tint, color: plat(sel).color }}>{plat(sel).label}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4, background: statusInfo(sel.status).chip, color: statusInfo(sel.status).dot }}>{statusInfo(sel.status).label}</span>
              </div>
              <X size={16} style={{ cursor: 'pointer', color: 'var(--gray-500)' }} onClick={() => setSel(null)} />
            </div>
            {postImage(sel) && <img src={postImage(sel)} alt="" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 6, margin: '10px 0' }} />}
            {sel.source_video_id && <div style={{ fontSize: 11, color: '#1a6fb0', margin: '8px 0', display: 'flex', alignItems: 'center', gap: 4 }}><Repeat size={12} /> Repurposed viral video{sel.source_title ? `: ${sel.source_title}` : ''}</div>}
            <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.45, whiteSpace: 'pre-wrap', margin: '8px 0' }}>{sel.caption || '(no caption)'}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', lineHeight: 1.7 }}>
              <div><strong>When:</strong> {postDate(sel) ? postDate(sel).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</div>
              {sel.created_by && <div><strong>By:</strong> {sel.created_by}</div>}
              {sel.utm_campaign && <div><strong>Campaign:</strong> {sel.utm_campaign}</div>}
              {sel.link && <div style={{ wordBreak: 'break-all' }}><strong>Link:</strong> {sel.link}</div>}
              {sel.status === 'failed' && sel.error && <div style={{ color: '#c0392b' }}><strong>Error:</strong> {sel.error}</div>}
            </div>
            {sel.status === 'scheduled' && onCancel && (
              <button onClick={() => { onCancel(sel.id); setSel(null); }} style={{ marginTop: 10, width: '100%', padding: 8, background: '#fff', color: '#c0392b', border: '1px solid #f3c0b8', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat' }}>Cancel this scheduled post</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
